import React from "react";
import { useAppDispatch, useAppSelector } from "../../../hook/redux";
import { toolbarStyles, isDesktop } from "../utils";
import FlipHorizontal from "./components/FlipHorizontal";
import FlipVertical from "./components/FlipVertical";
import RotateSelector from "./components/RotateSelector";
import UploadSelector from "./components/UploadSelector";
import ZoomIn from "./components/ZoomIn";
import ZoomOut from "./components/ZoomOut";
import { replaceImageInContainer } from "./imageToolbarUtils";
import * as fabric from 'fabric'

const placeholderUrl = process.env.NODE_ENV === 'development'
                       ? process.env.PLACEHOLDER_URL_DEV
                       : process.env.PLACEHOLDER_URL_PROD


export const checkAndAdjustRotation = (
  object: fabric.Object,
  clipPath: fabric.Object,
  newAngle: number
): void => {

  const realWidth = object.width * object.scaleX;
  const realHeight = object.height * object.scaleY;

  if (
    ((newAngle >= 90 && newAngle <= 179) ||
      (newAngle >= 270 && newAngle <= 359)) && realWidth < clipPath.height || realHeight < clipPath.width
  ) {

    const scale = Math.max(
      clipPath.width  / object.height,
      clipPath.height  / object.width
    );
    object.set({
      scaleX: scale,
      scaleY: scale,
    });
  }
};

function ImageEditor( { canvas } ) {
  const { selectedLayout } = useAppSelector(state => state.editor);
  const object = canvas.current?.getActiveObject();
  const disabled = object?._element.src === placeholderUrl;
  const desktop = isDesktop();

  const dispatch = useAppDispatch();


  /**
   * Transforms the image on the canvas based on the provided event, type, and value.
   *
   * @param {object} event - The event object that triggered the transformation.
   * @param {string} type - The type of transformation to perform (e.g. "rotate", "zoom", "flip").
   * @param {number} value - The value to apply for the transformation.
   */
  const transformImage = ( event, type: string, value: number ) => {
    if ( object ) {
      event.stopPropagation();

      const clipPath = object.clipPath;
      switch ( type ) {
        case "rotate":
          const newAngle = (object.angle + value) % 360;

          checkAndAdjustRotation(object, clipPath, newAngle);

          object.set( "angle", newAngle );

          canvas.current?.renderAll();

          replaceImageInContainer( object, clipPath );

          break;

        case "zoom":
          let newScaleX = object.scaleX * (1 + value);
          let newScaleY = object.scaleY * (1 + value);

          let newWidth = object.width * newScaleX;
          let newHeight = object.height * newScaleY;

          if ( newWidth < clipPath.width || newHeight < clipPath.height ) {
            break;
          }

          const clipPathCenter = clipPath.getCenterPoint();

          const imageCenter = {
            x: object.left,
            y: object.top,
          };
          
          const offsetX = clipPathCenter.x - imageCenter.x;
          const offsetY = clipPathCenter.y - imageCenter.y;

          object.set( {
            scaleX: newScaleX,
            scaleY: newScaleY,
            left: object.left - offsetX * value,
            top: object.top - offsetY * value,
          } );

          canvas.current?.renderAll();

          replaceImageInContainer( object, clipPath );

          break;

        case "flip":
          object.set( `flip${ value }`, !object[ `flip${ value }` ] );
          break;

        default:
          break;
      }
      dispatch({
        type: "editor/updateCurrentDataFabricArray",
        payload: {
          layoutId: selectedLayout,
          fabricElements: JSON.stringify(canvas.current.toJSON()),
        }
      });
      canvas.current?.renderAll();
    }
  };

    return (
    <div
      style={ desktop ? toolbarStyles.desktop.container : toolbarStyles.mobile.container }
      className="d-flex bg-white h-md-100"
      data-test="layout-menu-image"
    >
      <div
        style={ desktop ? toolbarStyles.desktop.iconsWrapper : toolbarStyles.mobile.iconsWrapper }
        className="icons-toolbar h-md-100"
      >
        <UploadSelector object={ object } canvas={ canvas } />
        <RotateSelector onTransform={ transformImage } disabled={ disabled } />
        <ZoomIn onTransform={ transformImage } disabled={ disabled } />
        <ZoomOut onTransform={ transformImage } disabled={ disabled } />
        <FlipHorizontal onTransform={ transformImage } disabled={ disabled } />
        <FlipVertical onTransform={ transformImage } disabled={ disabled } />
      </div>
    </div>
    );

    }

    export default ImageEditor;